import React, { useMemo, useState } from 'react';
import { Box, Group, Stack, Text, Badge, ScrollArea, Card, Avatar, Select, SegmentedControl } from '@mantine/core';
import { FileText, Calendar, AlertCircle, Clock, Lock } from 'lucide-react';
import { Loan } from '../types/loan';
interface LOBoardViewProps {
  loans: Record<string, Loan[]>;
  onLoanClick?: (loan: Loan) => void;
}
type QuickFilter = 'Active' | 'Reassigned' | 'Nurture' | 'Funded';
type GroupByOption = 'Stage' | 'Last Contact' | 'Rate Lock' | 'Loan Purpose' | 'Loan Type' | 'Close Date' | 'Processor' | 'Condition Count';
const CURRENT_LO = 'Jake Morrison';
const COLUMN_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ec4899', '#06b6d4', '#f97316', '#6366f1' // indigo
];
export function LOBoardView({
  loans,
  onLoanClick
}: LOBoardViewProps) {
  const [quickFilter, setQuickFilter] = useState<QuickFilter>('Active');
  const [groupBy, setGroupBy] = useState<GroupByOption>('Stage');
  // Flatten all loans
  const allLoans = useMemo(() => Object.values(loans).flat(), [loans]);
  // 1. Apply Quick Filters (LO's own deals + Status)
  const filteredLoans = useMemo(() => {
    return allLoans.filter(loan => loan.loanOfficer === CURRENT_LO && (loan.loanStatus || 'Active') === quickFilter);
  }, [allLoans, quickFilter]);
  // 2. Calculate KPIs
  const kpis = useMemo(() => {
    const today = new Date();
    let closing7 = 0;
    let closing30 = 0;
    let overdueItems = 0;
    filteredLoans.forEach(loan => {
      overdueItems += loan.overdueActionItems || 0;
      if (loan.closingDate) {
        const closeDate = new Date(loan.closingDate);
        const diffTime = closeDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays >= 0 && diffDays <= 7) closing7++;
        if (diffDays >= 0 && diffDays <= 30) closing30++;
      }
    });
    return {
      closing7,
      closing30,
      overdueItems
    };
  }, [filteredLoans]);
  // 3. Group and Sort Loans
  const groupedLoans = useMemo(() => {
    const groups: Record<string, Loan[]> = {};
    filteredLoans.forEach(loan => {
      let key = 'Unknown';
      switch (groupBy) {
        case 'Stage':
          // Find which original section this loan belonged to
          key = Object.keys(loans).find(k => loans[k].some(l => l.id === loan.id)) || 'Unknown';
          break;
        case 'Last Contact':
          key = loan.lastContactDate || 'No Contact';
          break;
        case 'Rate Lock':
          key = loan.lockStatus || 'Not Locked';
          break;
        case 'Loan Purpose':
          key = loan.loanPurpose || 'Unknown';
          break;
        case 'Loan Type':
          key = loan.loanType || 'Unknown';
          break;
        case 'Close Date':
          key = loan.closingDate || 'TBD';
          break;
        case 'Processor':
          key = loan.processor || 'Unassigned';
          break;
        case 'Condition Count':
          const openConditions = [...loan.outstandingConditions, ...loan.loanOfficerConditions].filter(c => !c.completed).length;
          if (openConditions === 0) key = '0 Conditions';else if (openConditions <= 3) key = '1-3 Conditions';else if (openConditions <= 7) key = '4-7 Conditions';else key = '8+ Conditions';
          break;
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(loan);
    });
    // Sort each group by stalest (highest daysInStage) first
    Object.keys(groups).forEach(key => {
      groups[key].sort((a, b) => b.daysInStage - a.daysInStage);
    });
    return groups;
  }, [filteredLoans, groupBy, loans]);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const getDaysColor = (days: number) => {
    if (days >= 6) return 'red';
    if (days >= 3) return 'orange';
    return 'gray';
  };
  return <Stack gap="lg" data-id="element-350">
      {/* Controls & KPIs Bar */}
      <Group justify="space-between" align="flex-end" data-id="element-351">
        <Stack gap="xs" data-id="element-352">
          <Text size="sm" fw={600} c="dimmed" tt="uppercase" data-id="element-353">
            My Pipeline View
          </Text>
          <SegmentedControl value={quickFilter} onChange={val => setQuickFilter(val as QuickFilter)} data={[{
          label: 'Active Pipeline',
          value: 'Active'
        }, {
          label: 'Reassigned',
          value: 'Reassigned'
        }, {
          label: 'Nurtures',
          value: 'Nurture'
        }, {
          label: 'Funded',
          value: 'Funded'
        }]} styles={{
          root: {
            backgroundColor: 'rgba(255,255,255,0.05)'
          },
          label: {
            color: 'rgba(255,255,255,0.7)'
          },
          indicator: {
            backgroundColor: '#c41e3a'
          } // NMHL Red
        }} data-id="element-354" />
        </Stack>

        <Group gap="xl" data-id="element-355">
          <Group gap="md" data-id="element-356">
            <Box ta="center" data-id="element-357">
              <Text size="xs" c="dimmed" tt="uppercase" fw={700} data-id="element-358">
                Closing &lt; 7 Days
              </Text>
              <Text size="xl" fw={800} c="white" data-id="element-359">
                {kpis.closing7}
              </Text>
            </Box>
            <Box ta="center" data-id="element-360">
              <Text size="xs" c="dimmed" tt="uppercase" fw={700} data-id="element-361">
                Closing &lt; 30 Days
              </Text>
              <Text size="xl" fw={800} c="white" data-id="element-362">
                {kpis.closing30}
              </Text>
            </Box>
            <Box ta="center" data-id="element-363">
              <Text size="xs" c="red" tt="uppercase" fw={700} data-id="element-364">
                Overdue Items
              </Text>
              <Text size="xl" fw={800} c="red" data-id="element-365">
                {kpis.overdueItems}
              </Text>
            </Box>
          </Group>

          <Select label="Group By" value={groupBy} onChange={val => setGroupBy(val as GroupByOption)} data={['Stage', 'Last Contact', 'Rate Lock', 'Loan Purpose', 'Loan Type', 'Close Date', 'Processor', 'Condition Count']} styles={{
          input: {
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderColor: 'rgba(255,255,255,0.1)',
            color: 'white'
          },
          label: {
            color: 'rgba(255,255,255,0.7)'
          }
        }} data-id="element-366" />
        </Group>
      </Group>

      {/* Kanban Board */}
      <ScrollArea type="auto" offsetScrollbars data-id="element-367">
        <Group align="flex-start" wrap="nowrap" gap="md" pb="md" style={{
        minWidth: 'max-content'
      }} data-id="element-368">
          {Object.entries(groupedLoans).map(([groupName, groupLoans], index) => {
          const totalVolume = groupLoans.reduce((sum, l) => sum + l.amount, 0);
          const columnColor = COLUMN_COLORS[index % COLUMN_COLORS.length];
          return <Box key={groupName} style={{
            width: 320,
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderTop: `3px solid ${columnColor}`,
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '800px'
          }} data-id="element-369">
                  {/* Column Header */}
                  <Box style={{
              padding: '16px',
              borderBottom: `2px solid ${columnColor}22`,
              backgroundColor: `${columnColor}0D`,
              borderTopLeftRadius: '9px',
              borderTopRightRadius: '9px'
            }} data-id="element-370">
                    <Group justify="space-between" mb="xs" data-id="element-371">
                      <Text fw={700} style={{
                  color: 'rgba(255, 255, 255, 0.9)'
                }} data-id="element-372">
                        {groupName}
                      </Text>
                      <Badge variant="filled" style={{
                  backgroundColor: `${columnColor}33`,
                  color: columnColor,
                  border: `1px solid ${columnColor}55`
                }} data-id="element-373">
                        {groupLoans.length}
                      </Badge>
                    </Group>
                    <Text fw={600} style={{
                color: 'rgba(255, 255, 255, 0.6)'
              }} data-id="element-374">
                      {formatCurrency(totalVolume)}
                    </Text>
                  </Box>

                  {/* Column Content */}
                  <ScrollArea style={{
              flex: 1
            }} p="sm" data-id="element-375">
                    <Stack gap="sm" data-id="element-376">
                      {groupLoans.map(loan => {
                  const openConditions = [...loan.outstandingConditions, ...loan.loanOfficerConditions].filter(c => !c.completed).length;
                  const hasOverdue = (loan.overdueActionItems || 0) > 0;
                  return <Card key={loan.id} onClick={() => onLoanClick?.(loan)} style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.06)',
                    border: hasOverdue ? '2px solid #c41e3a' // NMHL Red for overdue
                    : '1px solid rgba(255, 255, 255, 0.08)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    overflow: 'visible',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      transform: 'translateY(-2px)'
                    }
                  }} padding="md" radius="md" data-id="element-377">
                            {/* Overdue Pulse Indicator */}
                            {hasOverdue && <Box style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 14,
                      height: 14,
                      borderRadius: '50%',
                      backgroundColor: '#c41e3a',
                      boxShadow: '0 0 0 4px rgba(196, 30, 58, 0.3)',
                      animation: 'pulse 2s infinite'
                    }} data-id="element-378" />}

                            <Group justify="space-between" align="flex-start" mb="sm" data-id="element-379">
                              <Group gap="xs" data-id="element-380">
                                <FileText size={16} color="rgba(255, 255, 255, 0.5)" data-id="element-381" />
                                <Box data-id="element-382">
                                  <Text fw={700} size="sm" c="white" lh={1.2} data-id="element-383">
                                    {loan.clientName}
                                  </Text>
                                  <Text size="xs" c="dimmed" lh={1.2} data-id="element-384">
                                    #{loan.loanNumber}
                                  </Text>
                                </Box>
                              </Group>
                              <Badge variant="light" color={getDaysColor(loan.daysInStage)} size="sm" radius="sm" data-id="element-385">
                                {loan.daysInStage}d
                              </Badge>
                            </Group>

                            <Group justify="space-between" align="flex-end" mt="md" data-id="element-386">
                              <Stack gap={4} data-id="element-387">
                                {/* Open Conditions */}
                                <Group gap={6} data-id="element-388">
                                  <AlertCircle size={14} color={openConditions > 0 ? '#fb923c' : 'gray'} data-id="element-389" />
                                  <Text size="xs" c={openConditions > 0 ? '#fb923c' : 'dimmed'} data-id="element-390">
                                    {openConditions} Action Items
                                  </Text>
                                </Group>
                                {/* Rate Lock */}
                                <Group gap={6} data-id="element-391">
                                  <Lock size={14} color="rgba(255, 255, 255, 0.4)" data-id="element-392" />
                                  <Text size="xs" c="dimmed" data-id="element-393">
                                    {loan.lockStatus || 'Not Locked'}
                                  </Text>
                                </Group>
                              </Stack>
                              <Text fw={700} size="md" c="white" data-id="element-394">
                                {formatCurrency(loan.amount)}
                              </Text>
                            </Group>
                          </Card>;
                })}
                      {groupLoans.length === 0 && <Box py="xl" ta="center" data-id="element-395">
                          <Text size="sm" c="dimmed" data-id="element-396">
                            No loans in this group
                          </Text>
                        </Box>}
                    </Stack>
                  </ScrollArea>
                </Box>;
        })}
        </Group>
      </ScrollArea>
    </Stack>;
}